import * as Sentry from '@sentry/react'
import type { SearchClient } from 'instantsearch.js'

import { SentryErrorType } from 'Constants/errors'

const TRACKED_SEARCH_ERROR = Symbol('tracked-search-error')

class MalformedSearchResponseError extends Error {
  constructor(message: string) {
    super(`Malformed search response: ${message}`)
    this.name = 'MalformedSearchResponseError'
  }
}

export class TrackedSearchError extends Error {
  readonly [TRACKED_SEARCH_ERROR] = true

  constructor(
    message: string,
    readonly hasActiveRefinements: boolean,
    readonly isLatestRequest: () => boolean,
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.name = 'TrackedSearchError'
  }
}

const hasFilterValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some(hasFilterValue)
  }

  return typeof value === 'string'
    ? value.trim().length > 0
    : value !== null && value !== undefined
}

interface TrackedSearchRequest {
  indexName?: string
  params?: {
    facetFilters?: unknown
    numericFilters?: unknown
    tagFilters?: unknown
  }
}

export interface SearchRequestTracker {
  latestRequestId: number
}

const requestHasActiveRefinements = (requests: TrackedSearchRequest[]) =>
  requests.some(request => {
    const { facetFilters, numericFilters, tagFilters } = request.params ?? {}

    return [facetFilters, numericFilters, tagFilters].some(hasFilterValue)
  })

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error)

const EMPTY_FACET_SEARCH_RESULT = {
  exhaustiveFacetsCount: true,
  facetHits: [],
  processingTimeMS: 0,
}

const reportSearchError = (
  error: Error,
  requests: TrackedSearchRequest[],
  extra: Record<string, unknown> = {},
) => {
  // typesense-js puts the request payload into httpBody, so it is not reported.
  const { httpStatus } = (error.cause ?? {}) as { httpStatus?: number }

  Sentry.captureException(error, {
    extra: {
      ...extra,
      httpStatus,
      indexNames: requests.map(({ indexName }) => indexName),
      type: SentryErrorType.TYPESENSE_ERROR,
    },
  })
}

const hasSearchHits = (result: unknown) =>
  typeof result === 'object' &&
  result !== null &&
  !Array.isArray(result) &&
  Array.isArray((result as { hits?: unknown }).hits)

const assertSearchResponse = (response: unknown, requestCount: number) => {
  const results = (response as { results?: unknown } | null)?.results

  if (!Array.isArray(results)) {
    throw new MalformedSearchResponseError('results is not an array')
  }

  if (results.length !== requestCount) {
    throw new MalformedSearchResponseError(
      `expected ${requestCount} results, received ${results.length}`,
    )
  }

  if (!results.every(hasSearchHits)) {
    throw new MalformedSearchResponseError('a result is missing its hits array')
  }
}

export const isTrackedSearchError = (
  error: unknown,
): error is TrackedSearchError =>
  error instanceof Error &&
  TRACKED_SEARCH_ERROR in error &&
  error[TRACKED_SEARCH_ERROR] === true

export const createTrackedSearchClient = (
  searchClient: SearchClient,
  requestTracker: SearchRequestTracker = { latestRequestId: 0 },
): SearchClient => {
  const search = (async (requests: TrackedSearchRequest[]) => {
    requestTracker.latestRequestId += 1

    const requestId = requestTracker.latestRequestId
    const hasActiveRefinements = requestHasActiveRefinements(requests)

    try {
      const response = await searchClient.search(requests)

      assertSearchResponse(response, requests.length)

      return response
    } catch (error) {
      const trackedError = new TrackedSearchError(
        getErrorMessage(error),
        hasActiveRefinements,
        () => requestId === requestTracker.latestRequestId,
        { cause: error },
      )

      reportSearchError(trackedError, requests, { hasActiveRefinements })

      throw trackedError
    }
  }) as SearchClient['search']

  const { searchForFacetValues } = searchClient

  const trackedSearchForFacetValues = (async (
    requests: TrackedSearchRequest[],
  ) => {
    try {
      return await searchForFacetValues?.(requests)
    } catch (error) {
      // Facet search failures never reach the InstantSearch error state, so
      // resolve empty hits instead of leaving an unhandled rejection.
      reportSearchError(
        new Error(`Facet search failed: ${getErrorMessage(error)}`, {
          cause: error,
        }),
        requests,
      )

      return requests.map(() => EMPTY_FACET_SEARCH_RESULT)
    }
  }) as SearchClient['searchForFacetValues']

  return {
    ...searchClient,
    search,
    ...(searchForFacetValues && {
      searchForFacetValues: trackedSearchForFacetValues,
    }),
  }
}
